import React, { useState, useEffect, useCallback, useRef, ReactElement } from 'react';

import { Link } from 'components/Link/Link';
import { type Option } from 'types';

import IconQuestion from 'icons/ic_ErrorQuestion_black.svg';
import styles from './SelectMenu.module.scss';

interface SelectProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange'> {
  options: Option[];
  onChange?: (value: string) => void;
  defaultButtonContent?: ReactElement;
  value?: string | null;
  buttonStyle: string;
  align: 'left' | 'right' | 'center';
  lineBeforeLast?: boolean;
  size?: 'small' | 'large';
  downIcon?: string | null;
  questionMark?: boolean;
  disabled?: boolean;
  openOnHover?: boolean;
  forceUseDefaultButtonContent?: boolean;
  height?: number;
  width?: number;
  maxHeight?: number;
  textAlignment?: 'left' | 'center' | 'right';
}

export const SelectMenu: React.FC<SelectProps> = ({
  options,
  onChange,
  defaultButtonContent,
  value,
  buttonStyle,
  align,
  lineBeforeLast,
  size = 'large',
  downIcon = 'mm-angle-down',
  questionMark,
  disabled,
  openOnHover,
  forceUseDefaultButtonContent,
  height,
  width,
  textAlignment,
  maxHeight,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  const elementRef = useRef<HTMLDivElement>(null);

  const shouldFlipWhenOpen = downIcon && downIcon.includes('angle-down');

  const handleDomClick = useCallback((e: Event) => {
    const target = e.target as HTMLDivElement;

    if (!elementRef.current?.contains(target)) {
      if (openRef.current) {
        setOpen(false);
      }
    }
  }, []);

  const handleCloseIfEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleDomClick);
    document.addEventListener('keydown', handleCloseIfEscape);

    return () => {
      document.removeEventListener('click', handleDomClick);
      document.removeEventListener('keydown', handleCloseIfEscape);
    };
  }, [handleDomClick, handleCloseIfEscape]);

  const toggle = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setOpen(!open);
  };

  const handleClick = (option: Option): void => {
    const { value: _value, type } = option;

    setOpen(false);

    if (type === 'button' && option.callback) {
      option.callback();
    }

    if (_value && onChange) {
      onChange(_value);
    }

    return undefined;
  };

  const valueToShow = (() => {
    if (value && !forceUseDefaultButtonContent) {
      const option = options.find((option) => option.value === value);

      if (option) {
        return option.content;
      }
    }

    if (defaultButtonContent) {
      return defaultButtonContent;
    }

    return options[0].content;
  })();

  const unclickable = (() => {
    if (onChange) {
      return false;
    }

    const hasCallback = options.some((option) => {
      return (option.type === 'button' && option.callback) || (option.type === 'link' && option.url);
    });

    return !hasCallback;
  })();

  return (
    <div
      style={{ pointerEvents: disabled ? 'none' : undefined }}
      className={`${styles.Outer} ${styles[size]}`}
      data-testid="select-menu-outer"
      onMouseEnter={() => {
        if (openOnHover && options.length) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (openOnHover && options.length) {
          setOpen(false);
        }
      }}
      ref={elementRef}
    >
      {options.length ? (
        <button
          type="button"
          onClick={toggle}
          className={buttonStyle}
          aria-expanded={open}
          data-testid="select-menu-button"
          {...rest}
        >
          {valueToShow}{' '}
          {downIcon ? <i className={`${downIcon} ${open && shouldFlipWhenOpen ? styles.flip : ''}`} /> : null}
          {questionMark && <img src={IconQuestion} alt="Question mark icon" />}
        </button>
      ) : (
        <span data-testid="select-menu-span-for-no-options" className={buttonStyle}>
          {valueToShow}
        </span>
      )}
      <div
        className={`${styles.menuOuter} ${open ? styles.open : null} ${styles[align]} ${styles[size]} ${
          unclickable ? styles.unclickable : null
        }`}
        style={{
          ...(height ? { height, overflow: 'auto' } : {}),
          ...(width ? { width } : {}),
          ...(maxHeight ? { maxHeight, overflow: 'auto' } : {})
        }}
        data-testid="select-menu-menu"
        aria-hidden={!open}
        tabIndex={open ? undefined : -1}
      >
        <ul>
          {options.map((option: Option, index: number) => {
            const { value: _value, content, type, hidden } = option;

            if (hidden) {
              return null;
            }

            const isActive = _value === value;
            const isLast = index === options.length - 1;
            const showPreLine = isLast && lineBeforeLast;

            const innerJsx = (
              <div className={styles.innerJsx}>
                {content}
                {isActive && (
                  <div className={styles.optionSelectedCheck}>
                    <i className="mm-check" />
                  </div>
                )}
              </div>
            );

            return (
              <li key={_value} className={`${showPreLine ? styles.preLine : ''} ${isActive ? styles.active : ''}`}>
                {(() => {
                  switch (type) {
                    case 'link':
                      return (
                        <Link
                          href={option.url}
                          testId={`select-menu-option-${_value}`}
                          onClick={() => handleClick(option)}
                          hidden={open ? undefined : true}
                          target={option.openNewPage === true ? '_blank' : '_self'}
                        >
                          {innerJsx}
                        </Link>
                      );
                    case 'button':
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(option);
                          }}
                          className={option.isRedHoverBackground ? styles.SelectButtonRed : ''}
                          data-testid={`select-menu-option-${_value}`}
                          aria-hidden={open ? undefined : true}
                          tabIndex={open ? undefined : -1}
                          style={textAlignment ? { textAlign: textAlignment } : undefined}
                        >
                          {innerJsx}
                        </button>
                      );
                    default:
                      return (
                        <div className={styles.selectText} data-testid={`select-menu-option-${_value}`}>
                          {innerJsx}
                        </div>
                      );
                  }
                })()}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
